import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkle,
  Star,
  Globe,
  MagnifyingGlass,
  Warning,
  ArrowRight,
  Trophy,
  ClockCounterClockwise,
  Trash,
} from "@phosphor-icons/react";
import { TopNav } from "@/components/gbp/TopNav";
import {
  prospectingSearch,
  listProspectingRuns,
  getProspectingRun,
  deleteProspectingRun,
} from "@/lib/api";

const SAMPLE = `commercial cleaning dandenong
carpet cleaning dandenong
office cleaning dandenong`;

function fmt(n) {
  if (n === null || n === undefined || n === "") return "—";
  return Number(n).toLocaleString();
}

function fmtRating(r) {
  if (r === null || r === undefined || r === "") return "—";
  return Number(r).toFixed(1);
}

export default function Prospecting() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);

  const loadRuns = useCallback(async () => {
    try {
      const res = await listProspectingRuns(30);
      setRuns(res.items || []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const parsed = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const overCap = parsed.length > 10;

  const search = async () => {
    if (parsed.length === 0) {
      toast.error("Enter at least one keyword");
      return;
    }
    if (overCap) {
      toast.error("Max 10 keywords per search");
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const res = await prospectingSearch(parsed.slice(0, 10));
      setData(res);
      toast.success(
        `${res.summary.ai_visible} AI Visible · ${res.summary.not_visible} prospects · saved`,
      );
      loadRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const openRun = async (runId) => {
    setLoading(true);
    setData(null);
    try {
      const res = await getProspectingRun(runId);
      setData(res);
      setText((res.keywords || []).join("\n"));
      toast.message("Loaded saved run — no SerpApi calls used", {
        description: `${res.summary.ai_visible} AI Visible · ${res.summary.not_visible} prospects`,
      });
    } catch (e) {
      toast.error("Could not load that run");
    } finally {
      setLoading(false);
    }
  };

  const removeRun = async (runId) => {
    if (!window.confirm("Delete this saved run?")) return;
    try {
      await deleteProspectingRun(runId);
      toast.success("Deleted");
      if (data?.id === runId) setData(null);
      loadRuns();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="prospecting-page" className="min-h-screen bg-white">
      <TopNav />
      <main className="mx-auto max-w-6xl px-6 pb-24 pt-8 md:px-10">
        <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-electric/30 bg-electric-soft px-2.5 py-1 text-xs font-medium text-electric">
          <Sparkle size={12} weight="fill" /> AI Prospecting
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
          Find businesses Google&apos;s AI isn&apos;t recommending
        </h1>
        <p className="mt-1 max-w-3xl font-sans text-sm text-muted-foreground">
          Enter up to 10 niche+area keywords. We check who Google&apos;s AI
          Mode and Local Pack are surfacing for each one. Anyone visible on
          Maps but missing from AI/Pack is a prime outreach prospect.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-[1.4fr_auto]">
          <div className="rounded-md border border-border bg-white p-3 transition-colors focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/30">
            <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Keywords · one per line · max 10
            </label>
            <textarea
              data-testid="prospecting-keywords"
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={SAMPLE}
              className="mt-1 w-full resize-y bg-transparent font-mono text-sm text-ink placeholder:text-muted-foreground focus:outline-none"
            />
            <div className="mt-1 flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <span data-testid="prospecting-keyword-count">
                {parsed.length} / 10 keyword{parsed.length === 1 ? "" : "s"}
              </span>
              {overCap ? (
                <span className="text-[#C92A2A]">only first 10 will be used</span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            data-testid="prospecting-search-btn"
            onClick={search}
            disabled={loading || parsed.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-electric px-5 py-3 font-sans text-sm font-semibold text-white transition-transform hover:-translate-y-[1px] hover:bg-electric-hover disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {loading ? (
              <>
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Searching…
              </>
            ) : (
              <>
                <MagnifyingGlass size={14} weight="bold" />
                Search
                <ArrowRight size={14} weight="bold" />
              </>
            )}
          </button>
        </div>

        {runs.length > 0 && (
          <section
            data-testid="saved-runs"
            className="mt-6 rounded-lg border border-border bg-surface/40 p-3"
          >
            <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              <ClockCounterClockwise size={13} weight="bold" />
              Saved searches · click to reopen without using SerpApi
            </div>
            <div className="flex flex-wrap gap-2">
              {runs.map((r) => (
                <div
                  key={r.id}
                  data-testid={`saved-run-${r.id}`}
                  className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-white pl-3 pr-1.5 py-1 text-xs text-ink transition-colors hover:border-electric"
                >
                  <button
                    type="button"
                    onClick={() => openRun(r.id)}
                    className="inline-flex items-center gap-1.5 hover:text-electric"
                    title={r.keywords?.join(" · ")}
                  >
                    <span className="font-medium">
                      {(r.keywords || []).slice(0, 2).join(" · ")}
                      {r.keywords && r.keywords.length > 2
                        ? ` +${r.keywords.length - 2}`
                        : ""}
                    </span>
                    <span className="rounded-sm bg-electric-soft px-1.5 py-0.5 font-mono text-[10px] font-medium text-electric">
                      {r.summary?.ai_visible ?? 0}✦ / {r.summary?.not_visible ?? 0}◯
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {r.created_at
                        ? new Date(r.created_at).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })
                        : ""}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid={`delete-run-${r.id}`}
                    onClick={() => removeRun(r.id)}
                    className="grid h-5 w-5 place-items-center rounded-full text-muted-foreground opacity-0 transition-opacity hover:bg-[#FCE8E8] hover:text-[#C92A2A] group-hover:opacity-100"
                    aria-label="Delete saved run"
                  >
                    <Trash size={11} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {loading && (
          <div className="mt-8 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-16 rounded-lg gbp-shimmer" />
            ))}
          </div>
        )}

        {data && !loading && (
          <>
            <div
              data-testid="prospecting-summary"
              className="mt-8 rounded-md border border-border bg-surface px-4 py-3 font-sans text-sm text-ink"
            >
              <span className="font-semibold">{data.summary.total}</span>{" "}
              businesses found across {data.keywords.length} keyword
              {data.keywords.length === 1 ? "" : "s"}.{" "}
              <span className="font-semibold text-[#0C6B2E]">
                {data.summary.ai_visible}
              </span>{" "}
              are AI Visible.{" "}
              <span className="font-semibold text-[#C92A2A]">
                {data.summary.not_visible}
              </span>{" "}
              are not visible and are prospecting opportunities.
            </div>

            {data.ai_visible.length > 0 && (
              <Bucket
                title="AI Visible"
                subtitle={`${data.ai_visible.length} · Google's AI is already recommending these`}
                tone="visible"
                testid="bucket-ai-visible"
                items={data.ai_visible}
                mode="visible"
              />
            )}

            {data.not_visible.length > 0 && (
              <Bucket
                title="Not Visible"
                subtitle={`${data.not_visible.length} · outreach opportunities, sorted by reviews`}
                tone="not-visible"
                testid="bucket-not-visible"
                items={data.not_visible}
                mode="not-visible"
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Bucket({ title, subtitle, tone, testid, items, mode }) {
  const borderClass =
    tone === "visible"
      ? "border-l-4 border-l-[#0C6B2E]"
      : "border-l-4 border-l-[#C92A2A]";
  return (
    <section
      data-testid={testid}
      className={`mt-8 overflow-hidden rounded-lg bg-white ${borderClass}`}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          {tone === "visible" ? (
            <Trophy size={16} weight="fill" className="text-[#0C6B2E]" />
          ) : (
            <Warning size={16} weight="fill" className="text-[#C92A2A]" />
          )}
          <div>
            <div className="font-display text-lg font-bold text-ink">
              {title}
            </div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              {subtitle}
            </div>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            tone === "visible"
              ? "border-[#A3E0B5] bg-[#E6F4EA] text-[#0C6B2E]"
              : "border-[#FECACA] bg-[#FCE8E8] text-[#C92A2A]"
          }`}
        >
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${
              tone === "visible" ? "bg-[#0C6B2E]" : "bg-[#C92A2A]"
            }`}
          />
          {tone === "visible" ? "AI Visible" : "Not Visible"}
        </span>
      </div>
      <div className="divide-y divide-border/60">
        {items.map((b, i) => (
          <BusinessRow key={`${b.name}-${i}`} b={b} mode={mode} index={i} />
        ))}
      </div>
    </section>
  );
}

function BusinessRow({ b, mode, index }) {
  const posEntries = Object.entries(b.positions || {});
  return (
    <div
      data-testid={`prospect-row-${mode}-${index}`}
      className="grid grid-cols-1 gap-2 px-4 py-3 md:grid-cols-[1.4fr_auto_auto] md:items-center"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="truncate font-sans font-semibold text-ink">
            {b.name}
          </div>
          <span className="inline-flex items-center gap-1 font-mono tabular-nums text-xs text-ink">
            <Star size={11} weight="fill" className="text-yellow-500" />
            {fmtRating(b.rating)}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {fmt(b.reviews)} reviews
          </span>
          {b.website ? (
            <a
              href={b.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-electric hover:underline"
            >
              <Globe size={11} weight="bold" />
              website
            </a>
          ) : null}
        </div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {mode === "visible" && posEntries.length > 0
            ? posEntries.map(([kw, pos]) => (
                <span
                  key={kw}
                  className="inline-flex items-center gap-1 rounded-md border border-electric/30 bg-electric-soft px-2 py-0.5 font-mono text-[10px] text-electric"
                >
                  ✦ {pos}
                  <span className="font-sans lowercase text-electric/80">
                    · {kw}
                  </span>
                </span>
              ))
            : (b.found_in || []).map((kw) => (
                <span
                  key={kw}
                  className="inline-flex items-center rounded-md bg-black/5 px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
                >
                  {kw}
                </span>
              ))}
        </div>
      </div>
    </div>
  );
}
